import type { ProviderKind } from '@hangar/shared';
import type { ProviderTokenVerifyResult } from '@/api/schemas';
import { cn } from '@/lib/utils';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PROVIDER_TOKEN_GUIDE, needsAccount, providerVerifyRows, providerVerifyVerdict, type ProviderVerifyTone } from './providerTokenModel';
import type { VerifyProviderToken } from './useVerifyProviderToken';

/** Which token to create, where, and why that kind: the steps an operator follows at the provider before pasting it. */
export function ProviderTokenGuide({ kind }: { kind: ProviderKind }) {
  const guide = PROVIDER_TOKEN_GUIDE[kind];
  return (
    <section aria-labelledby="provider-token-guide-title" data-testid="provider-token-guide" data-kind={kind} className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:p-4">
      <h3 id="provider-token-guide-title" className="text-[13px] font-semibold text-foreground">
        Create a {guide.tokenName.toLowerCase()}
      </h3>
      <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-xs leading-relaxed text-muted-foreground marker:text-subtle" data-testid="provider-token-steps">
        {guide.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="text-xs leading-relaxed text-subtle">{guide.why}</p>
      <Button asChild size="sm" variant="outline" className="self-start">
        <a href={guide.home} target="_blank" rel="noreferrer noopener" data-testid="provider-token-open">
          <Icon name="open_in_new" size={16} />
          {guide.homeLabel}
        </a>
      </Button>
    </section>
  );
}

/** The accounts (Expo) or workspaces (Bitrise) the token sees: pick one instead of typing it. */
export function AccountChoices({ label, result, chosen, onPick }: { label: string; result: ProviderTokenVerifyResult; chosen: string; onPick: (externalAccount: string) => void }) {
  return (
    <div role="radiogroup" aria-label={label} data-testid="provider-account-choices" className="flex flex-col gap-1.5">
      {result.accounts.map((a) => {
        const on = a.externalAccount === chosen;
        return (
          <button key={a.externalAccount} type="button" role="radio" aria-checked={on} data-testid={`provider-account-choice-${a.externalAccount}`} onClick={() => onPick(a.externalAccount)}
            className={cn('flex min-h-11 items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors duration-150', on ? 'border-primary/40 bg-primary/5' : 'bg-card hover:bg-secondary')}>
            <Icon name={on ? 'radio_button_checked' : 'radio_button_unchecked'} size={18} className={on ? 'text-primary' : 'text-subtle'} />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold text-foreground">{a.displayName}</span>
              {a.displayName !== a.externalAccount ? <span className="block truncate font-mono text-[11px] text-subtle">{a.externalAccount}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Where the account comes from: the list the token sees, else a typed name or slug (the provider did not list it). */
export function AccountSource({ kind, check, account, typed, error, onChange }: { kind: ProviderKind; check: VerifyProviderToken; account: string; typed: string; error: string | undefined; onChange: (value: string) => void }) {
  const guide = PROVIDER_TOKEN_GUIDE[kind];
  const result = check.result;
  if (!check.ready) return <p data-testid="provider-account-waiting" className="text-xs text-subtle">Paste the token: Hangar asks {kind === 'eas' ? 'Expo' : 'Bitrise'} what it reaches and checks it before anything is stored.</p>;
  if (result && result.accounts.length > 0) {
    return (
      <FormField id="provider-account-choices" label={guide.accountLabel} required error={error}>
        <AccountChoices label={guide.accountLabel} result={result} chosen={account} onPick={onChange} />
      </FormField>
    );
  }
  if (!result) return null;
  return (
    <FormField id="provider-account-external" label={guide.accountLabel} required hint={guide.accountHint} error={error}>
      <Input data-testid="provider-account-external" value={typed} onChange={(e) => onChange(e.target.value)} placeholder={guide.accountPlaceholder} className="font-mono sm:text-[13px]" spellCheck={false} autoComplete="off" maxLength={120} {...fieldA11y('provider-account-external', error, 'hint')} />
    </FormField>
  );
}

const LOOK: Record<ProviderVerifyTone, { icon: string; tone: string; panel: string }> = {
  passed: { icon: 'check_circle', tone: 'text-succeeded', panel: 'border-succeeded/30 bg-succeeded/10' },
  failed: { icon: 'error', tone: 'text-failed', panel: 'border-failed/30 bg-failed/10' },
  skipped: { icon: 'info', tone: 'text-subtle', panel: 'border-border bg-card' },
};

/** What the provider said about the token: a verdict, one row per check, and the button to ask again. */
export function ProviderTokenVerify({ kind, check }: { kind: ProviderKind; check: VerifyProviderToken }) {
  const result = check.result;
  if (!check.ready) return null;
  const verdict = result ? providerVerifyVerdict(kind, result) : null;
  return (
    <div className="flex flex-col gap-2">
      {result && verdict && !(needsAccount(result) && result.accounts.length > 0) ? (
        <div data-testid="provider-token-verify-result" data-ok={result.ok} data-tone={verdict.tone} role="status" className={cn('flex flex-col gap-2 rounded-lg border p-3 text-xs', LOOK[verdict.tone].panel)}>
          <p className="flex items-start gap-1.5 font-semibold text-foreground">
            <Icon name={LOOK[verdict.tone].icon} size={16} fill className={cn('shrink-0', LOOK[verdict.tone].tone)} />
            {verdict.text}
          </p>
          <ul className="flex flex-col gap-1">
            {providerVerifyRows(kind, result).map((row) => (
              <li key={row.key} data-check={row.key} data-tone={row.tone} className="flex items-start gap-1.5">
                <Icon name={LOOK[row.tone].icon} size={14} className={cn('mt-px shrink-0', LOOK[row.tone].tone)} />
                <span className="min-w-0 text-muted-foreground">
                  <span className="font-medium text-foreground">{row.label}</span>
                  {row.message ? <span className="break-words"> · {row.message}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <Button type="button" size="sm" variant="outline" className="self-start" data-testid="provider-token-verify" loading={check.busy} onClick={() => void check.verify().catch(() => undefined)}>
        <Icon name="verified_user" size={16} />
        {result ? 'Verify again' : 'Verify token'}
      </Button>
      {check.error ? <ProblemAlert error={check.error} testId="provider-token-verify-problem" /> : null}
    </div>
  );
}
